import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  LearningQuestion,
  LearningQuestionType,
  LearningResponseRow,
  QuestionSummary,
  FILTER_NONE,
  filterResponses,
  filterOptions,
  isLearningAvailable,
  summarizeLearning,
} from '../lib/learning-tabulacion';
import { palabra, plural } from '../lib/plurales';

// ── Types ──────────────────────────────────────────────────────────────────

type BlockType = 'text' | 'image' | 'video';
type QuestionType = LearningQuestionType;
type LearningStatus = 'borrador' | 'activo' | 'vencido';

interface Block {
  id: string;
  type: BlockType;
  content: string;
  requires_confirmation: boolean;
}

type Question = LearningQuestion;

interface LearningContent {
  id: string;
  company_id: string;
  title: string;
  description: string | null;
  status: LearningStatus;
  expires_at: string | null;
  blocks: Block[];
  questions: Question[];
  sucursal_options: string[];
  departamento_options: string[];
  created_at: string;
}

type LearningResponse = LearningResponseRow;

type ResultsTab = 'resumen' | 'participantes';

// Aviso que queda bajo la tarjeta después de "Copiar link": el link en un
// campo seleccionable si el portapapeles falló, y/o la advertencia de que
// el Learning no acepta respuestas todavía.
interface CopyNotice {
  itemId: string;
  url: string;
  clipboardFailed: boolean;
  inactive: boolean;
}

const STATUS_LABELS: Record<LearningStatus, string> = {
  borrador: 'Borrador',
  activo: 'Activo',
  vencido: 'Vencido',
};

const EMPTY_FORM = {
  title: '',
  description: '',
  status: 'borrador' as LearningStatus,
  expires_at: '',
  blocks: [] as Block[],
  questions: [] as Question[],
  sucursal_options: [] as string[],
  departamento_options: [] as string[],
};

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// ── Component ──────────────────────────────────────────────────────────────

export default function LearningAdminPage() {
  const { company } = useCompany();
  const { impersonated } = useImpersonation();
  // Empresa en pantalla: la impersonada (superadmin entrando como cliente)
  // o la propia — mismo criterio que Dashboard.tsx y las páginas psicosociales.
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id;

  const [items, setItems] = useState<LearningContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState<LearningContent | undefined | null>(undefined);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [optionInput, setOptionInput] = useState({ sucursal: '', departamento: '' });
  const [viewingResponsesFor, setViewingResponsesFor] = useState<LearningContent | null>(null);
  const [responses, setResponses] = useState<LearningResponse[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [viewingResponseDetail, setViewingResponseDetail] = useState<LearningResponse | null>(null);
  const [responsesError, setResponsesError] = useState<string | null>(null);
  const [resultsTab, setResultsTab] = useState<ResultsTab>('resumen');
  const [filters, setFilters] = useState({ sucursal: '', departamento: '' });

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyNotice, setCopyNotice] = useState<CopyNotice | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  const fetchItems = useCallback(async () => {
    if (!companyId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('learning_content')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (!error && data) setItems(data as LearningContent[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Al cambiar de empresa (entrar o salir de una impersonación) se cierran
  // las vistas abiertas: pertenecen a la empresa anterior.
  useEffect(() => {
    setViewingResponsesFor(null);
    setViewingResponseDetail(null);
    setEditTarget(undefined);
    setCopyNotice(null);
  }, [companyId]);

  async function openResponses(item: LearningContent) {
    setViewingResponsesFor(item);
    setResultsTab('resumen');
    setFilters({ sucursal: '', departamento: '' });
    setResponses([]);
    setResponsesError(null);
    setLoadingResponses(true);
    const { data, error } = await supabase
      .from('learning_responses')
      .select('id, email, nombre_apellido, sucursal, departamento, answers, submitted_at')
      .eq('learning_id', item.id)
      .order('submitted_at', { ascending: false });
    if (error) setResponsesError('No se pudieron cargar las respuestas');
    else setResponses((data ?? []) as LearningResponse[]);
    setLoadingResponses(false);
  }

  // Mismo patrón que el QR del cuestionario psicosocial: window.location.origin
  // (https://storsafe.app en producción; también funciona en local y previews).
  async function handleCopyLink(item: LearningContent) {
    const url = `${window.location.origin}/?learning=${item.id}`;
    const inactive = !isLearningAvailable(item, new Date());
    let clipboardFailed = false;
    try {
      // navigator.clipboard no existe fuera de un contexto seguro (http sin
      // localhost) y writeText puede rechazarse si el navegador lo bloquea.
      await navigator.clipboard.writeText(url);
    } catch {
      clipboardFailed = true;
    }
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    if (clipboardFailed) {
      setCopiedId(null);
    } else {
      setCopiedId(item.id);
      copiedTimer.current = setTimeout(() => setCopiedId(null), 2000);
    }
    setCopyNotice(clipboardFailed || inactive ? { itemId: item.id, url, clipboardFailed, inactive } : null);
  }

  function openCreate() {
    setForm({ ...EMPTY_FORM });
    setEditTarget(null);
  }

  function openEdit(item: LearningContent) {
    setForm({
      title: item.title,
      description: item.description ?? '',
      status: item.status,
      expires_at: item.expires_at ? item.expires_at.slice(0, 10) : '',
      blocks: item.blocks ?? [],
      questions: item.questions ?? [],
      sucursal_options: item.sucursal_options ?? [],
      departamento_options: item.departamento_options ?? [],
    });
    setEditTarget(item);
  }

  async function handleSave() {
    if (!companyId || !form.title.trim()) return;
    setSaving(true);
    const payload = {
      company_id: companyId,
      title: form.title.trim(),
      description: form.description.trim() || null,
      status: form.status,
      expires_at: form.expires_at || null,
      blocks: form.blocks,
      questions: form.questions,
      sucursal_options: form.sucursal_options,
      departamento_options: form.departamento_options,
    };
    if (editTarget) {
      await supabase.from('learning_content').update(payload).eq('id', editTarget.id);
    } else {
      await supabase.from('learning_content').insert(payload);
    }
    setSaving(false);
    setEditTarget(undefined);
    fetchItems();
  }

  // ── Blocks builder ──
  function addBlock(type: BlockType) {
    setForm(f => ({ ...f, blocks: [...f.blocks, { id: uid(), type, content: '', requires_confirmation: false }] }));
  }
  function updateBlock(id: string, patch: Partial<Block>) {
    setForm(f => ({ ...f, blocks: f.blocks.map(b => b.id === id ? { ...b, ...patch } : b) }));
  }
  function removeBlock(id: string) {
    setForm(f => ({ ...f, blocks: f.blocks.filter(b => b.id !== id) }));
  }

  function descargarRespuestaCSV(item: LearningContent, r: LearningResponse) {
    const rows = [
      ['Nombre', r.nombre_apellido],
      ['Correo', r.email],
      ['Sucursal', r.sucursal ?? ''],
      ['Departamento', r.departamento ?? ''],
      ['Fecha', new Date(r.submitted_at).toLocaleString()],
      [],
      ['Pregunta', 'Respuesta'],
      ...item.questions.map(q => [q.prompt, String(r.answers[q.id] ?? '')]),
    ];
    const csv = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `respuesta_${r.nombre_apellido.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function descargarMatrizCSV(item: LearningContent, allResponses: LearningResponse[]) {
    const headers = ['Nombre', 'Correo', 'Sucursal', 'Departamento', 'Fecha', ...item.questions.map(q => q.prompt)];
    const rows = allResponses.map(r => [
      r.nombre_apellido,
      r.email,
      r.sucursal ?? '',
      r.departamento ?? '',
      new Date(r.submitted_at).toLocaleString(),
      ...item.questions.map(q => String(r.answers[q.id] ?? '')),
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `respuestas_${item.title.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Questions builder ──
  function addQuestion() {
    setForm(f => ({ ...f, questions: [...f.questions, { id: uid(), type: 'open_text', prompt: '', options: [], scale_max: 10, required: true }] }));
  }
  function updateQuestion(id: string, patch: Partial<Question>) {
    setForm(f => ({ ...f, questions: f.questions.map(q => q.id === id ? { ...q, ...patch } : q) }));
  }
  function removeQuestion(id: string) {
    setForm(f => ({ ...f, questions: f.questions.filter(q => q.id !== id) }));
  }

  // ── Sucursal / Departamento options ──
  function addOption(field: 'sucursal_options' | 'departamento_options', value: string) {
    if (!value.trim()) return;
    setForm(f => ({ ...f, [field]: [...f[field], value.trim()] }));
  }
  function removeOption(field: 'sucursal_options' | 'departamento_options', idx: number) {
    setForm(f => ({ ...f, [field]: f[field].filter((_, i) => i !== idx) }));
  }

  // Filtros y resumen de la vista de resultados — se recalculan en el
  // navegador sobre las respuestas ya cargadas, sin consultas nuevas.
  const filteredResponses = useMemo(() => filterResponses(responses, filters), [responses, filters]);
  const summaries = useMemo(
    () => (viewingResponsesFor ? summarizeLearning(viewingResponsesFor.questions ?? [], filteredResponses) : []),
    [viewingResponsesFor, filteredResponses]
  );

  return (
    <div style={{ padding: 24 }}>
      {viewingResponsesFor ? (
        <LearningResultsView
          item={viewingResponsesFor}
          responses={responses}
          filteredResponses={filteredResponses}
          summaries={summaries}
          loading={loadingResponses}
          error={responsesError}
          tab={resultsTab}
          onTabChange={setResultsTab}
          filters={filters}
          onFiltersChange={setFilters}
          onBack={() => setViewingResponsesFor(null)}
          onViewDetail={setViewingResponseDetail}
          onDownloadRow={r => descargarRespuestaCSV(viewingResponsesFor, r)}
          onDownloadMatrix={() => descargarMatrizCSV(viewingResponsesFor, filteredResponses)}
        />
      ) : (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ color: '#f1f5f9', fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>Diseña tu propio Learning</h2>
        <button onClick={openCreate} style={primaryBtn}>+ Nuevo Learning</button>
      </div>

      {loading ? (
        <p style={{ color: '#94a3b8' }}>Cargando…</p>
      ) : items.length === 0 ? (
        <p style={{ color: '#94a3b8' }}>Todavía no has creado ningún Learning.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(item => (
            <div key={item.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: '#f1f5f9', fontWeight: 600 }}>{item.title}</div>
                  <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 2 }}>
                    {STATUS_LABELS[item.status]} · {item.blocks?.length ?? 0} bloques · {item.questions?.length ?? 0} preguntas
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleCopyLink(item)}
                    style={copiedId === item.id ? { ...ghostBtn, color: '#2FBF71', borderColor: 'rgba(47,191,113,0.5)' } : ghostBtn}
                  >
                    {copiedId === item.id ? '✓ Copiado' : 'Copiar link'}
                  </button>
                  <button onClick={() => openEdit(item)} style={ghostBtn}>Editar</button>
                  <button onClick={() => openResponses(item)} style={ghostBtn}>Ver respuestas</button>
                </div>
              </div>

              {copyNotice?.itemId === item.id && (
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {copyNotice.clipboardFailed && (
                        <span style={{ color: '#fbbf24', fontSize: '0.8rem' }}>No se pudo copiar automáticamente. Selecciona el link y cópialo:</span>
                      )}
                      {copyNotice.inactive && (
                        <span style={{ color: '#fbbf24', fontSize: '0.8rem' }}>
                          Este link no está activo: los participantes verán "Este formulario ya no está disponible"
                          {item.status !== 'activo' ? ` (estado: ${STATUS_LABELS[item.status]}).` : ' (fecha de caducidad vencida).'}
                        </span>
                      )}
                    </div>
                    <button onClick={() => setCopyNotice(null)} style={{ ...closeBtn, fontSize: '0.9rem' }} aria-label="Cerrar aviso">✕</button>
                  </div>
                  {copyNotice.clipboardFailed && (
                    <input readOnly value={copyNotice.url} onFocus={e => e.target.select()} autoFocus style={{ ...inp, fontFamily: 'monospace', fontSize: '0.8rem' }} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {editTarget !== undefined && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !saving && setEditTarget(undefined)}>
          <div style={{ ...modalBox, maxWidth: 720 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                {editTarget ? 'Editar Learning' : 'Nuevo Learning'}
              </h2>
              <button onClick={() => !saving && setEditTarget(undefined)} style={closeBtn}>✕</button>
            </div>

            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20, maxHeight: '70vh', overflowY: 'auto' }}>

              <Field label="Título *">
                <input style={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: VIH/SIDA — Educación y Prevención" />
              </Field>
              <Field label="Descripción">
                <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Estado">
                  <select style={sel} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as LearningStatus }))}>
                    {(Object.keys(STATUS_LABELS) as LearningStatus[]).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </Field>
                <Field label="Fecha de caducidad">
                  <input style={inp} type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
                </Field>
              </div>
              {/* Índice único learning_responses_learning_email_uniq (8cc779d). */}
              <p data-testid="learning-nota-un-correo" style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '-4px 0 0' }}>
                Cada correo puede responder una sola vez este formulario. Para repetir la capacitación con las mismas personas, crea un Learning nuevo.
              </p>

              {/* Sucursal / Departamento */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Opciones de Sucursal">
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input style={inp} value={optionInput.sucursal} onChange={e => setOptionInput(o => ({ ...o, sucursal: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { addOption('sucursal_options', optionInput.sucursal); setOptionInput(o => ({ ...o, sucursal: '' })); } }}
                      placeholder="Escribe y presiona Enter" />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {form.sucursal_options.map((opt, i) => (
                      <span key={i} style={tag}>{opt} <button onClick={() => removeOption('sucursal_options', i)} style={tagRemove}>✕</button></span>
                    ))}
                  </div>
                </Field>
                <Field label="Opciones de Departamento">
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input style={inp} value={optionInput.departamento} onChange={e => setOptionInput(o => ({ ...o, departamento: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { addOption('departamento_options', optionInput.departamento); setOptionInput(o => ({ ...o, departamento: '' })); } }}
                      placeholder="Escribe y presiona Enter" />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {form.departamento_options.map((opt, i) => (
                      <span key={i} style={tag}>{opt} <button onClick={() => removeOption('departamento_options', i)} style={tagRemove}>✕</button></span>
                    ))}
                  </div>
                </Field>
              </div>

              {/* Blocks builder */}
              <div style={{ borderTop: '1px solid #334155', paddingTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.9rem' }}>Bloques de contenido</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => addBlock('text')} style={ghostBtn}>+ Texto</button>
                    <button onClick={() => addBlock('image')} style={ghostBtn}>+ Imagen</button>
                    <button onClick={() => addBlock('video')} style={ghostBtn}>+ Video</button>
                  </div>
                </div>
                {form.blocks.map((block, i) => (
                  <div key={block.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Bloque {i + 1} · {block.type}</span>
                      <button onClick={() => removeBlock(block.id)} style={{ ...closeBtn, fontSize: '0.9rem' }}>✕</button>
                    </div>
                    {block.type === 'text' ? (
                      <textarea style={{ ...inp, minHeight: 60 }} value={block.content} onChange={e => updateBlock(block.id, { content: e.target.value })} placeholder="Texto del bloque…" />
                    ) : (
                      <input style={inp} value={block.content} onChange={e => updateBlock(block.id, { content: e.target.value })} placeholder={block.type === 'image' ? 'URL de la imagen' : 'URL del video (YouTube, etc.)'} />
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: '#94a3b8', fontSize: '0.8rem' }}>
                      <input type="checkbox" checked={block.requires_confirmation} onChange={e => updateBlock(block.id, { requires_confirmation: e.target.checked })} />
                      Requiere checkbox "He revisado detenidamente el contenido"
                    </label>
                  </div>
                ))}
              </div>

              {/* Questions builder */}
              <div style={{ borderTop: '1px solid #334155', paddingTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.9rem' }}>Preguntas</span>
                  <button onClick={addQuestion} style={ghostBtn}>+ Pregunta</button>
                </div>
                {form.questions.map((q, i) => (
                  <div key={q.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Pregunta {i + 1}</span>
                      <button onClick={() => removeQuestion(q.id)} style={{ ...closeBtn, fontSize: '0.9rem' }}>✕</button>
                    </div>
                    <input style={{ ...inp, marginBottom: 6 }} value={q.prompt} onChange={e => updateQuestion(q.id, { prompt: e.target.value })} placeholder="Texto de la pregunta" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <select style={sel} value={q.type} onChange={e => updateQuestion(q.id, { type: e.target.value as QuestionType })}>
                        <option value="open_text">Texto abierto</option>
                        <option value="multiple_choice">Opción múltiple</option>
                        <option value="scale">Escala (1 a N)</option>
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: '0.8rem' }}>
                        <input type="checkbox" checked={q.required} onChange={e => updateQuestion(q.id, { required: e.target.checked })} />
                        Obligatoria
                      </label>
                    </div>
                    {q.type === 'multiple_choice' && (
                      <textarea style={{ ...inp, minHeight: 50, marginTop: 8 }} value={q.options.join('\n')}
                        onChange={e => updateQuestion(q.id, { options: e.target.value.split('\n') })}
                        placeholder={'Una opción por línea'} />
                    )}
                    {q.type === 'scale' && (
                      <input style={{ ...inp, marginTop: 8, maxWidth: 100 }} type="number" min={2} max={10} value={q.scale_max}
                        onChange={e => updateQuestion(q.id, { scale_max: Number(e.target.value) })} placeholder="Máximo (ej: 10)" />
                    )}
                  </div>
                ))}
              </div>

            </div>
            <div style={modalFooter}>
              <button onClick={() => !saving && setEditTarget(undefined)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} style={{ ...saveBtn, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Guardando…' : editTarget ? 'Guardar cambios' : 'Crear Learning'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingResponseDetail && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && setViewingResponseDetail(null)}>
          <div style={{ ...modalBox, maxWidth: 520 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                Respuesta de {viewingResponseDetail.nombre_apellido}
              </h2>
              <button onClick={() => setViewingResponseDetail(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', maxHeight: '65vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                {viewingResponseDetail.email} · {viewingResponseDetail.sucursal ?? '—'} · {viewingResponseDetail.departamento ?? '—'} · {new Date(viewingResponseDetail.submitted_at).toLocaleString()}
              </div>
              {Object.entries(viewingResponseDetail.answers).map(([questionId, answer]) => {
                const question = viewingResponsesFor?.questions.find(q => q.id === questionId);
                return (
                  <div key={questionId} style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{question?.prompt ?? questionId}</div>
                    <div style={{ color: '#e2e8f0' }}>{String(answer)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Vista de resultados (página completa: Resumen / Participantes) ────────

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  multiple_choice: 'Opción múltiple',
  open_text: 'Texto abierto',
  scale: 'Escala',
};

const BAR_GRADIENT = 'linear-gradient(90deg, #1D6FA3 0%, #2FBF71 100%)';

// Fecha y hora reales (submitted_at): se muestra el día en hora de Ecuador.
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-EC', {
    timeZone: 'America/Guayaquil', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// expires_at es una fecha de calendario elegida en un <input type="date">,
// guardada en una columna timestamptz como medianoche UTC
// ('2027-01-31T00:00:00+00:00'). Convertirla a hora de Ecuador mostraría el
// día anterior, así que se toma solo la parte AAAA-MM-DD — mismo criterio
// que learningExpiryInstantMs().
function fmtCalendarDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : fmtDate(value);
}

function LearningResultsView(props: {
  item: LearningContent;
  responses: LearningResponse[];
  filteredResponses: LearningResponse[];
  summaries: QuestionSummary[];
  loading: boolean;
  error: string | null;
  tab: ResultsTab;
  onTabChange: (tab: ResultsTab) => void;
  filters: { sucursal: string; departamento: string };
  onFiltersChange: (f: { sucursal: string; departamento: string }) => void;
  onBack: () => void;
  onViewDetail: (r: LearningResponse) => void;
  onDownloadRow: (r: LearningResponse) => void;
  onDownloadMatrix: () => void;
}) {
  const { item, responses, filteredResponses, summaries, loading, error, tab, filters } = props;
  const sucursales = filterOptions(item.sucursal_options ?? [], responses.map(r => r.sucursal));
  const departamentos = filterOptions(item.departamento_options ?? [], responses.map(r => r.departamento));
  const isFiltered = filters.sucursal !== '' || filters.departamento !== '';
  const available = isLearningAvailable(item, new Date());

  return (
    <div>
      <button onClick={props.onBack} style={{ ...ghostBtn, marginBottom: 14 }}>← Volver a Learnings</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>{item.title}</h2>
          <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: 4 }}>
            {STATUS_LABELS[item.status]}
            {item.expires_at && ` · vence ${fmtCalendarDate(item.expires_at)}`}
            {!available && ' · no acepta respuestas'}
            {' · '}{plural(responses.length, 'respuesta', 'respuestas')}
            {isFiltered && ` · ${filteredResponses.length} con los filtros aplicados`}
          </div>
        </div>
        {filteredResponses.length > 0 && (
          <button onClick={props.onDownloadMatrix} style={ghostBtn}>
            Descargar matriz CSV{isFiltered ? ` (${filteredResponses.length} filtradas)` : ''}
          </button>
        )}
      </div>

      {/* Pestañas */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #334155', marginBottom: 14 }}>
        {(['resumen', 'participantes'] as ResultsTab[]).map(t => (
          <button
            key={t}
            onClick={() => props.onTabChange(t)}
            style={{
              background: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: '0.88rem', fontWeight: 600,
              color: tab === t ? '#f1f5f9' : '#64748b',
              borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: tab === t ? '2px solid #2FBF71' : '2px solid transparent', marginBottom: -1,
            }}
          >
            {t === 'resumen' ? 'Resumen' : `Participantes (${filteredResponses.length})`}
          </button>
        ))}
      </div>

      {/* Filtros — aplican a ambas pestañas */}
      {(sucursales.options.length > 0 || sucursales.hasNone || departamentos.options.length > 0 || departamentos.hasNone) && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          {(sucursales.options.length > 0 || sucursales.hasNone) && (
            <div style={{ minWidth: 200 }}>
              <Field label="Sucursal">
                <select style={sel} value={filters.sucursal} onChange={e => props.onFiltersChange({ ...filters, sucursal: e.target.value })}>
                  <option value="">Todas</option>
                  {sucursales.options.map(o => <option key={o} value={o}>{o}</option>)}
                  {sucursales.hasNone && <option value={FILTER_NONE}>(Sin sucursal)</option>}
                </select>
              </Field>
            </div>
          )}
          {(departamentos.options.length > 0 || departamentos.hasNone) && (
            <div style={{ minWidth: 200 }}>
              <Field label="Departamento">
                <select style={sel} value={filters.departamento} onChange={e => props.onFiltersChange({ ...filters, departamento: e.target.value })}>
                  <option value="">Todos</option>
                  {departamentos.options.map(o => <option key={o} value={o}>{o}</option>)}
                  {departamentos.hasNone && <option value={FILTER_NONE}>(Sin departamento)</option>}
                </select>
              </Field>
            </div>
          )}
          {isFiltered && (
            <button onClick={() => props.onFiltersChange({ sucursal: '', departamento: '' })} style={{ ...ghostBtn, marginBottom: 2 }}>
              Quitar filtros
            </button>
          )}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#94a3b8' }}>Cargando…</p>
      ) : error ? (
        <p style={{ color: '#fca5a5' }}>{error}</p>
      ) : responses.length === 0 ? (
        <p style={{ color: '#94a3b8' }}>Todavía no hay respuestas.</p>
      ) : filteredResponses.length === 0 ? (
        <p style={{ color: '#94a3b8' }}>Ninguna respuesta coincide con los filtros.</p>
      ) : tab === 'resumen' ? (
        summaries.length === 0 ? (
          <p style={{ color: '#94a3b8' }}>Este Learning no tiene preguntas.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {summaries.map((s, i) => (
              <QuestionSummaryCard key={s.question.id} index={i + 1} summary={s} total={filteredResponses.length} />
            ))}
          </div>
        )
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left', color: '#94a3b8' }}>
                <th style={{ padding: '6px 8px' }}>Nombre</th>
                <th style={{ padding: '6px 8px' }}>Correo</th>
                <th style={{ padding: '6px 8px' }}>Sucursal</th>
                <th style={{ padding: '6px 8px' }}>Departamento</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
                <th style={{ padding: '6px 8px' }}>Ver</th>
              </tr>
            </thead>
            <tbody>
              {filteredResponses.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #1e293b', color: '#e2e8f0' }}>
                  <td style={{ padding: '6px 8px' }}>{r.nombre_apellido}</td>
                  <td style={{ padding: '6px 8px' }}>{r.email}</td>
                  <td style={{ padding: '6px 8px' }}>{r.sucursal ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{r.departamento ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{fmtDate(r.submitted_at)}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <button onClick={() => props.onViewDetail(r)} title="Ver respuesta" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>👁️</button>
                    <button onClick={() => props.onDownloadRow(r)} title="Descargar CSV" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>⬇️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HBar({ label, count, pct, muted }: { label: string; count: number; pct: number; muted?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 2fr) 3fr 90px', gap: 10, alignItems: 'center' }}>
      <span style={{ color: muted ? '#94a3b8' : '#e2e8f0', fontSize: '0.84rem', fontStyle: muted ? 'italic' : 'normal', overflowWrap: 'anywhere' }}>{label}</span>
      <div style={{ height: 10, background: '#0f172a', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: muted ? '#64748b' : BAR_GRADIENT, borderRadius: 5 }} />
      </div>
      <span style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'right' }}>{count} ({pct}%)</span>
    </div>
  );
}

function QuestionSummaryCard({ index, summary, total }: { index: number; summary: QuestionSummary; total: number }) {
  const [showIndividual, setShowIndividual] = useState(false);
  const q = summary.question;
  const typeLabel = summary.kind === 'scale' ? `Escala 1–${q.scale_max}` : QUESTION_TYPE_LABELS[summary.kind];

  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.92rem' }}>{index}. {q.prompt || '(pregunta sin texto)'}</span>
        <span style={{ color: '#64748b', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          {typeLabel} · {summary.answered} de {total} respondieron
        </span>
      </div>

      {summary.answered === 0 ? (
        <p style={{ color: '#64748b', fontSize: '0.84rem', margin: 0 }}>Nadie respondió esta pregunta.</p>
      ) : summary.kind === 'multiple_choice' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {summary.options.map(o => <HBar key={o.label} label={o.label} count={o.count} pct={o.pct} />)}
          {summary.orphans.length > 0 && (
            <div style={{ borderTop: '1px dashed #334155', paddingTop: 8, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ color: '#fbbf24', fontSize: '0.78rem' }}>
                Opciones que ya no existen (se editaron las opciones después de recibir estas respuestas):
              </span>
              {summary.orphans.map(o => <HBar key={o.label} label={o.label} count={o.count} pct={o.pct} muted />)}
            </div>
          )}
        </div>
      ) : summary.kind === 'scale' ? (
        <div>
          <div style={{ color: '#e2e8f0', fontSize: '0.86rem', marginBottom: 10 }}>
            Promedio <strong>{summary.average!.toLocaleString('es-EC', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</strong>
            <span style={{ color: '#94a3b8' }}> · mín {summary.min} · máx {summary.max}</span>
          </div>
          <ScaleDistribution distribution={summary.distribution} />
          {summary.outOfRange > 0 && (
            <div style={{ color: '#fbbf24', fontSize: '0.78rem', marginTop: 6 }}>
              {plural(summary.outOfRange, 'respuesta', 'respuestas')} fuera de la escala actual 1–{q.scale_max} (la escala se cambió después de {palabra(summary.outOfRange, 'recibirla', 'recibirlas')}); se {palabra(summary.outOfRange, 'incluye', 'incluyen')} en el promedio.
            </div>
          )}
          <button onClick={() => setShowIndividual(v => !v)} style={{ ...ghostBtn, marginTop: 10 }}>
            {showIndividual ? 'Ocultar respuestas individuales ▴' : 'Ver respuestas individuales ▾'}
          </button>
          {showIndividual && (
            <div style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {summary.entries.map(e => (
                <div key={e.response.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.82rem', color: '#e2e8f0', borderBottom: '1px solid #0f172a', padding: '3px 0' }}>
                  <span>{e.response.nombre_apellido} <span style={{ color: '#64748b' }}>· {e.response.sucursal ?? '—'} · {fmtDate(e.response.submitted_at)}</span></span>
                  <strong>{e.value}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {summary.entries.map(e => (
            <div key={e.response.id} style={{ borderLeft: '2px solid #334155', paddingLeft: 10 }}>
              <div style={{ color: '#e2e8f0', fontSize: '0.86rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>"{e.value}"</div>
              <div style={{ color: '#64748b', fontSize: '0.76rem', marginTop: 2 }}>
                — {e.response.nombre_apellido} · {e.response.sucursal ?? '—'} · {fmtDate(e.response.submitted_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Barras verticales pequeñas, una por valor de la escala (1..scale_max).
function ScaleDistribution({ distribution }: { distribution: { value: number; count: number }[] }) {
  const maxCount = Math.max(1, ...distribution.map(d => d.count));
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 90 }}>
      {distribution.map(d => (
        <div key={d.value} style={{ flex: 1, maxWidth: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}>
          <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{d.count}</span>
          <div style={{ width: '100%', height: `${(d.count / maxCount) * 56}px`, minHeight: d.count > 0 ? 3 : 1, background: d.count > 0 ? BAR_GRADIENT : '#334155', borderRadius: 3 }} />
          <span style={{ color: '#64748b', fontSize: '0.72rem' }}>{d.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

// ── Styles (clonados de TrainingsPage.tsx) ──────────────────────────────────

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' };
const modalBox: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 580, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' };
const modalHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' };
const modalFooter: React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' };
const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel: React.CSSProperties = { ...inp, cursor: 'pointer' };
const closeBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn: React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn: React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
const primaryBtn: React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const ghostBtn: React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const tag: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.06)', border: '1px solid #334155', borderRadius: 20, padding: '3px 10px', fontSize: '0.78rem', color: '#e2e8f0' };
const tagRemove: React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', padding: 0, lineHeight: 1 };
